import React, { useEffect, useMemo, useState } from "react";
import { Upload } from "lucide-react";
import AdminPromotionsService from "../../../Modules/Services/admin.promotions.service";
import API_BASE_URL from "../../../config";
import { AdminModal, AdminFlash } from "../../../Components/Admin/AdminOverlays";

function isoToDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ManagePromotions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [flash, setFlash] = useState({ type: "", text: "" });

  const showFlash = (type, text) => {
    setFlash({ type, text });
    if (text) window.setTimeout(() => setFlash({ type: "", text: "" }), 4000);
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = await AdminPromotionsService.list();
      setRows(data);
    } catch {
      showFlash("error", "Не удалось загрузить акции.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    if (editing) {
      setDescription(editing.description || "");
      setSortOrder(editing.sort_order ?? 0);
      setStartsAt(isoToDatetimeLocal(editing.starts_at));
      setEndsAt(isoToDatetimeLocal(editing.ends_at));
      setImageFile(null);
    } else {
      setDescription("");
      setSortOrder(0);
      setStartsAt("");
      setEndsAt("");
      setImageFile(null);
    }
  }, [editing, modalOpen]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [rows]
  );

  const handleSave = async () => {
    const trimmed = description.trim();
    if (!trimmed) {
      showFlash("error", "Введите описание акции.");
      return;
    }
    try {
      const payload = {
        description: trimmed,
        sortOrder: Number(sortOrder) || 0,
        startsAt: startsAt ? new Date(startsAt).toISOString() : null,
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        imageFile,
      };

      if (editing) {
        await AdminPromotionsService.update(editing.id, payload);
        showFlash("success", "Акция обновлена.");
      } else {
        await AdminPromotionsService.create(payload);
        showFlash("success", "Акция создана.");
      }

      setModalOpen(false);
      setEditing(null);
      fetchRows();
    } catch {
      showFlash("error", "Не удалось сохранить.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить акцию?")) return;
    try {
      await AdminPromotionsService.delete(id);
      showFlash("success", "Акция удалена.");
      fetchRows();
    } catch {
      showFlash("error", "Не удалось удалить.");
    }
  };

  return (
    <div>
      <h1 className="admin-page-title">Акции</h1>

      {flash.text ? (
        <AdminFlash
          type={flash.type}
          message={flash.text}
          onClose={() => setFlash({ type: "", text: "" })}
        />
      ) : null}

      <div className="admin-toolbar">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Создать акцию
        </button>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-spinner">Загрузка…</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Порядок</th>
                <th>Картинка</th>
                <th>Описание</th>
                <th>Даты</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((record) => (
                <tr key={record.id}>
                  <td>{record.id}</td>
                  <td>{record.sort_order ?? 0}</td>
                  <td>
                    {record.image_url ? (
                      <img
                        className="admin-thumb"
                        src={`${API_BASE_URL}${record.image_url}`}
                        alt=""
                      />
                    ) : (
                      <span className="admin-muted">—</span>
                    )}
                  </td>
                  <td style={{ maxWidth: 360, whiteSpace: "pre-wrap" }}>
                    {record.description}
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>
                    <div>С: {record.starts_at ? String(record.starts_at) : "—"}</div>
                    <div>По: {record.ends_at ? String(record.ends_at) : "—"}</div>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        type="button"
                        className="btn btn--primary btn--small"
                        onClick={() => {
                          setEditing(record);
                          setModalOpen(true);
                        }}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => handleDelete(record.id)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminModal
        open={modalOpen}
        title={editing ? "Редактировать акцию" : "Новая акция"}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setModalOpen(false);
                setEditing(null);
              }}
            >
              Отмена
            </button>
            <button type="button" className="btn btn--primary" onClick={handleSave}>
              Сохранить
            </button>
          </>
        }
      >
        <div className="admin-field">
          <label className="admin-field__label">Описание</label>
          <textarea
            className="admin-textarea"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label">Порядок (0 — внизу)</label>
          <input
            type="number"
            min={0}
            step={1}
            className="admin-input"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="promo-start">
            Действует с
          </label>
          <input
            id="promo-start"
            type="datetime-local"
            className="admin-input"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="promo-end">
            Действует по
          </label>
          <input
            id="promo-end"
            type="datetime-local"
            className="admin-input"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <span className="admin-field__label">Картинка</span>
          <label className="btn btn--outline btn--small" style={{ cursor: "pointer" }}>
            <Upload size={14} style={{ marginRight: 6 }} />
            Выбрать файл
            <input
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              onChange={(e) =>
                setImageFile(e.target.files?.[0] || null)
              }
            />
          </label>
          {editing?.image_url && !imageFile ? (
            <p className="admin-muted" style={{ marginTop: "0.5rem" }}>
              Сейчас: <code>{editing.image_url}</code>
            </p>
          ) : null}
        </div>
      </AdminModal>
    </div>
  );
};

export default ManagePromotions;
